import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate } from "@tanstack/react-router";

import { DocumentSlideout } from "components/bid_module/requirement_summary_report_section/DocumentSlideout";
import { CustomerDocumentDrawer } from "components/customer_documents/CustomerDocumentDrawer";
import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import {
  updateBidReportAnswersCache,
  useGetBidReportAnswers,
} from "lib/hooks/api/bid/answers/useGetBidReportAnswers";
import { useStreamBidReport } from "lib/hooks/api/bid/answers/useStreamBidReport";
import { useBidReport } from "lib/hooks/api/bid/bid_reports/useBidReport";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useRestricedViewNotice } from "lib/hooks/useRestrictedRowClick";
import {
  useBidOrchestratorStream,
  useBidOrchestratorStreamContext,
} from "lib/providers/bid_orchestrator/BidOrchestratorStreamProvider";
import { routes, routeSegments } from "lib/routes";
import { EventData, EventNames, TrackingProvider, useTracking } from "lib/tracking";
import { BidReportType } from "lib/types/graphQLEnums";
import { BidReportEntry_Answer_Sources } from "../../../lib/generated/app-service-gql/graphql";
import { Flex } from "../../../styles/utility-components";
import { DetailsContent } from "../../app_layout/DetailsLayout";
import { BID_REPORT_LOADING_COPY } from "../common_report_components/BidReportLoadingPage";
import BidReportLoadingState from "../common_report_components/BidReportLoadingState";
import { BidReportSection } from "../common_report_components/BidReportSection";
import { isNoticeSource } from "../common_report_components/DocumentSources";
import { ReportNoticeDetails } from "../common_report_components/ReportNoticeDetails";
import { AnswerSource } from "../types";
import { BidNoBidReportHeader } from "./BidNoBidReportHeader";

import css from "./BidNoBidReport.module.scss";

type Props = {
  bidReportId: string;
  bidId: string;
};
export function BidNoBidReport({ bidReportId, bidId }: Props) {
  const { logEvent } = useTracking();
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();

  const [documentData, setDocumentData] = useState<AnswerSource | null>(null);
  // Documents slideout open by default
  const [slideoutOpen, setSlideoutOpen] = useState(true);

  const [debug, setDebug] = useState(false);

  const { data: bidInfo, isLoading: isLoadingBidInfo } = useBidReport(bidReportId);

  const { trackOrchestrator } = useBidOrchestratorStreamContext();
  // Live orchestrator state for this bid drives the per-section streaming indicator.
  // Unused (and inert) when the qualification-report flag is off and we fall back
  // to the non-streaming useGenerateAllAnswers path.
  const { isRequirementSummaryStreaming } = useBidOrchestratorStream(bidId);

  const noticeId = bidInfo?.notice?.guid;
  const noticeTitle = bidInfo?.notice?.name ?? undefined;
  const buyerName = bidInfo?.notice?.buyers?.[0]?.name;
  const buyerId = bidInfo?.notice?.buyers?.[0]?.guid;
  const urlShareSignature = bidInfo?.publicHash ?? undefined;

  const { data, isLoading: isQuerying } = useGetBidReportAnswers(bidReportId);

  const { mutate: streamBidReport, isLoading: isDispatching } = useStreamBidReport({
    onMutate: () => {
      // Wipe the cached old report so it can't flash between mutation success and the
      // first streamed chunk — chunks repopulate the cache as they arrive.
      updateBidReportAnswersCache(queryClient, bidReportId, () => ({ result: [] }));
    },
    onSuccess: (data) => {
      toast.info("Regenerating bid report");
      trackOrchestrator(bidId, data.streamBidReport.runId, {
        qualificationReport: false,
        requirementSummary: true,
      });
    },
    onError: () => toast.error("Failed to regenerate bid report"),
  });

  const onRegenerateBidReport = useCallback(
    async (eventName: EventNames) => {
      logEvent(eventName, {
        "Action type": "Button clicked",
        "Report type": "Requirement summary",
      });
      streamBidReport({
        input: { bidReportId, reportType: BidReportType.RequirementSummary },
      });
    },
    [bidReportId, logEvent, streamBidReport],
  );

  const { viewRecord } = useRestricedViewNotice("Title click");

  const handleNoticeLink = useCallback(() => {
    if (currentUser) {
      if (noticeId) {
        viewRecord(noticeId);
      }
    } else {
      // Open in a new tab
      window.open(`${routes.notices}/${noticeId}`, "_blank");
    }
  }, [noticeId, viewRecord, currentUser]);

  const onSelectDocument = useCallback(
    async (
      source: BidReportEntry_Answer_Sources,
      trackingContext: EventNames,
      trackingData: EventData,
    ) => {
      if (isNoticeSource(source, noticeId)) {
        handleNoticeLink();
      } else {
        if (!source.documentId) {
          toast.error("Failed to load document");
          return;
        }
        setDocumentData({
          ...source,
          documentId: source.documentId,
          locations: source.locations ?? [],
        });
      }

      logEvent(trackingContext, {
        ...trackingData,
        "Record GUID": noticeId,
        "Bid ID": bidId,
        "Report type": "Requirement summary",
      });
    },
    [bidId, handleNoticeLink, logEvent, noticeId],
  );

  const hasEditAccess = !!currentUser && currentUser.team.id === bidInfo?.teamId;

  // Keep the page mounted through dispatch and streaming so the cache fills in live.
  const isStreamingFlow = isDispatching || isRequirementSummaryStreaming;

  if ((isLoadingBidInfo || isQuerying) && !isStreamingFlow) {
    const { title, description } = BID_REPORT_LOADING_COPY[routeSegments.requirementSummary];
    return <BidReportLoadingState title={title} description={description} bidId={bidId} />;
  }

  // If there's no data and no bidInfo, there is no bid, so redirect the user
  const noBidData = !isLoadingBidInfo && !bidInfo && !isQuerying && !data;

  if (noBidData) {
    return <Navigate to={routes.bids} />;
  }

  return (
    <TrackingProvider data={{ "Report type": "Requirement summary" }}>
      <div className={css.qualificationReportPage}>
        <BidNoBidReportHeader
          bidReportId={bidReportId}
          bidInfo={bidInfo}
          bidId={bidId}
          data={data}
          urlShareSignature={urlShareSignature}
          slideoutOpen={slideoutOpen}
          setSlideoutOpen={setSlideoutOpen}
          onRegenerateBidReport={() =>
            onRegenerateBidReport(EventNames.qualificationReportRegenerated)
          }
        />
        <DetailsContent className={css.pageContent}>
          <div className={css.answersWrapper}>
            <ReportNoticeDetails
              title="Requirement summary"
              noticeId={noticeId}
              noticeTitle={noticeTitle}
              buyerName={buyerName}
              buyerId={buyerId}
              reportId={bidReportId}
              bidId={bidId}
              bidName={bidInfo?.bid?.name}
              hasEditAccess={hasEditAccess}
              reportType={BidReportType.RequirementSummary}
            />
            <div className={css.qaReportContent}>
              {data?.map((section) => (
                <BidReportSection
                  key={section.groupName}
                  answerGroup={section}
                  bidReportId={bidReportId}
                  reportType={BidReportType.RequirementSummary}
                  debugMode={debug}
                  onSelectDocument={onSelectDocument}
                  slideoutOpen={slideoutOpen}
                  noticeId={noticeId}
                  noticeTitle={noticeTitle}
                  hasEditAccess={hasEditAccess}
                  isStreaming={isRequirementSummaryStreaming}
                  bidId={bidId}
                  bidName={bidInfo?.bid?.name ?? ""}
                />
              ))}
            </div>
            <Flex gap="24px" flexDirection="column">
              <p className={css.explainerText}>
                *Stotles AI can make mistakes, important information should be verified.
              </p>
              {currentUser?.isAdmin && (
                <Button
                  as="button"
                  buttonType="secondary"
                  fullWidth={false}
                  style={{ width: "fit-content" }}
                  onClick={() => setDebug(!debug)}
                >
                  Toggle debug
                </Button>
              )}
            </Flex>
          </div>
          {slideoutOpen && (
            <DocumentSlideout
              bidId={bidId}
              hasEditAccess={hasEditAccess}
              onSelectDocument={(source) =>
                onSelectDocument(source, EventNames.tenderDocumentOpened, {
                  "Action type": "Document reader opened",
                  "Document name": source.fileName,
                })
              }
              onRegenerateBidReport={() =>
                onRegenerateBidReport(EventNames.additionalDocumentsRegenerated)
              }
            />
          )}

          {documentData && (
            <CustomerDocumentDrawer
              source={documentData}
              isOpen={Boolean(documentData)}
              onClose={() => setDocumentData(null)}
            />
          )}
        </DetailsContent>
      </div>
    </TrackingProvider>
  );
}
